import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageWrapper } from '../components/PageWrapper';
import { JournalAttachmentImage } from '../components/JournalAttachmentImage';
import { Textarea } from '../components/ui/textarea';
import {
  getEntry,
  getEntries,
  getSettings,
  formatDisplayDate,
  addCommentToEntry,
  updateEntryWithAIResponse,
  isAICompanionEnabled,
  saveEntry,
} from '../utils/storage';
import { getDailyCompletionMessage } from '../utils/aiCompanion';
import { deleteCloudJournalPhoto } from '../utils/cloudApi';

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const collectLegacyJournalAnswers = (e) => {
  const answers = {};
  for (let i = 1; i <= 10; i += 1) {
    const k = `answer${i}`;
    const v = e[k];
    if (v && v !== '–') answers[k] = String(v);
  }
  return answers;
};

function HistoricalAttachmentTile({ att, onPick }) {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <div className="space-y-1 min-w-0">
      <button
        type="button"
        onClick={() => onPick(att)}
        className="w-full rounded-xl overflow-hidden border border-theme-secondary focus:outline-none focus:ring-2 focus:ring-wisteria-200"
        aria-label="View photo"
      >
        <JournalAttachmentImage
          url={att.url}
          storagePath={att.storagePath}
          alt={String(att.caption || '').slice(0, 120)}
          className="w-full aspect-square object-cover bg-wisteria-50/30"
          onUnavailable={() => setHidden(true)}
        />
      </button>
      {att.caption ? (
        <p className="font-body text-[11px] text-secondary leading-snug line-clamp-3 break-words [overflow-wrap:anywhere]">
          {att.caption}
        </p>
      ) : null}
    </div>
  );
}

export const HistoricalView = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const settings = getSettings();

  const [entry, setEntry] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState(null);
  const [photoDeleteConfirmOpen, setPhotoDeleteConfirmOpen] = useState(false);

  const photoLightboxActionClass =
    'flex-1 min-h-[48px] rounded-2xl px-4 py-3 bg-theme-primary text-primary font-body text-sm border border-theme-secondary shadow-ambient';

  useEffect(() => {
    const loaded = getEntry(date);
    if (loaded) {
      setEntry(loaded);
    }
    setIsLoaded(true);
  }, [date]);

  useEffect(() => {
    if (!photoLightbox) {
      setPhotoDeleteConfirmOpen(false);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (photoDeleteConfirmOpen) setPhotoDeleteConfirmOpen(false);
      else setPhotoLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoLightbox, photoDeleteConfirmOpen]);

  if (!isLoaded) {
    return (
      <PageWrapper className="bg-app">
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 size={24} className="animate-spin text-secondary" />
        </div>
      </PageWrapper>
    );
  }

  if (!entry) {
    return (
      <PageWrapper className="bg-app">
        <div className="flex flex-col items-center justify-center min-h-screen px-6">
          <p className="text-secondary font-body text-sm">Entry not found</p>
          <button
            onClick={() => navigate('/history')}
            className="mt-8 underline text-wisteria-600 font-body text-sm"
          >
            Back to History
          </button>
        </div>
      </PageWrapper>
    );
  }

  const goals = entry.goalSnapshots || [];
  const legacyJournalLines = Object.values(collectLegacyJournalAnswers(entry));
  const comments = entry.comments || [];
  const attachments = Array.isArray(entry.attachments)
    ? entry.attachments.filter((a) => a && (a.url || a.storagePath))
    : [];

  const reloadEntry = () => {
    const loaded = getEntry(date);
    setEntry(loaded || null);
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await deleteCloudJournalPhoto(date, photoId);
      const row = getEntries()[date] || {};
      const prev = Array.isArray(row.attachments) ? row.attachments : [];
      const next = prev.filter((a) => String(a?.id) !== String(photoId));
      saveEntry(date, { ...row, attachments: next });
      reloadEntry();
      setPhotoLightbox(null);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(String(e?.message || e || 'Could not delete photo'));
    }
  };

  const handleSaveComment = async () => {
    if (!commentText.trim()) return;

    setIsSavingComment(true);
    const fallbackMessage = 'Thanks for adding to your journal.';
    let responseMessage = fallbackMessage;
    let shouldPersistResponse = false;

    // Add comment to entry
    addCommentToEntry(date, commentText);
    
    // Generate AI response with comment context
    const aiEnabled = isAICompanionEnabled();
    if (aiEnabled) {
      try {
        // Prepare data for AI including the comment
        const formData = {
          goals: entry.goals || {},
          answers: {
            ...collectLegacyJournalAnswers(entry),
            new_comment: commentText.trim(),
          },
        };

        const aiResult = await getDailyCompletionMessage({
          formData,
          goals,
        });
        if (aiResult?.message) {
          responseMessage = aiResult.message;
        }
        if (aiResult?.usedAI && aiResult?.message) {
          shouldPersistResponse = true;
        }
      } catch (error) {
        console.error('Error generating AI response for comment:', error);
      }
    }

    // Only persist when a real AI response was generated.
    if (shouldPersistResponse) {
      updateEntryWithAIResponse(date, responseMessage);
    }

    // Navigate to AIResponse screen
    navigate('/ai-response', {
      state: {
        response: responseMessage,
        userName: settings?.name || 'friend',
        returnTo: `/historical-view/${date}`,
      },
    });

    setCommentText('');
    setIsSavingComment(false);
  };

  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-6 min-w-0 overflow-x-hidden">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <button
            onClick={() => navigate('/history')}
            className="p-2 rounded-full hover:bg-wisteria-50 transition-colors"
            data-testid="historical-back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={24} strokeWidth={1.5} className="text-secondary" />
          </button>
          <h1 className="font-heading text-2xl font-light text-primary">
            {formatDisplayDate(date)}
          </h1>
        </motion.div>

        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="mb-8"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-3">
              Photos
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {attachments.map((att) => (
                <HistoricalAttachmentTile
                  key={att.id || att.storagePath || att.url}
                  att={att}
                  onPick={setPhotoLightbox}
                />
              ))}
            </div>
          </motion.div>
        )}

        {photoLightbox ? (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="absolute inset-0 z-0 cursor-default border-0 bg-black/70 p-0"
              onClick={() => {
                if (!photoDeleteConfirmOpen) setPhotoLightbox(null);
              }}
              aria-label="Close photo preview"
            />
            <div className="relative z-10 flex w-full max-w-md max-h-[85vh] flex-col items-center gap-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              <div className="max-w-full rounded-lg">
                <JournalAttachmentImage
                  url={photoLightbox.url}
                  storagePath={photoLightbox.storagePath}
                  alt=""
                  className="max-h-[55vh] w-auto max-w-full rounded-lg object-contain"
                  onUnavailable={() => setPhotoLightbox(null)}
                />
              </div>
              <div className="flex w-full max-w-sm gap-3">
                <button
                  type="button"
                  className={photoLightboxActionClass}
                  onClick={() => setPhotoLightbox(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className={photoLightboxActionClass}
                  onClick={() => setPhotoDeleteConfirmOpen(true)}
                >
                  Delete
                </button>
              </div>
            </div>
            {photoDeleteConfirmOpen ? (
              <div
                className="absolute inset-0 z-[220] flex items-center justify-center bg-black/60 p-4"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="historical-photo-delete-confirm-title"
              >
                <div className="w-full max-w-sm rounded-2xl bg-theme-light border border-theme-secondary p-6 shadow-lg">
                  <p
                    id="historical-photo-delete-confirm-title"
                    className="font-body text-sm text-primary text-center mb-5"
                  >
                    Are you sure you want to delete this photo?
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className={photoLightboxActionClass}
                      onClick={() => setPhotoDeleteConfirmOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={photoLightboxActionClass}
                      onClick={() => {
                        setPhotoDeleteConfirmOpen(false);
                        void handleDeletePhoto(String(photoLightbox.id || '').trim());
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Goals Section */}
        {goals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4">
              Goals
            </h2>
            <div className="space-y-2">
              {goals.map((goal, index) => {
                const status = entry.goals?.[goal.id];
                const isCompleted = status?.completed || status?.easyActivity;
                const displayText = goal.text;

                if (!isCompleted) return null;

                return (
                  <motion.div
                    key={goal.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-wisteria-100 border border-wisteria-200"
                  >
                    <span className="text-wisteria-700 font-medium">✓</span>
                    <span className="font-body text-sm text-primary">{cap(displayText)}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {legacyJournalLines.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 space-y-4"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4">
              That day in your words
            </h2>
            {legacyJournalLines.map((text, index) => (
              <motion.div
                key={`legacy-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + index * 0.05 }}
              >
                <p className="font-body text-sm text-primary leading-relaxed bg-theme-light rounded-2xl p-4 border border-theme-secondary break-words [overflow-wrap:anywhere]">
                  {text}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* AI Response Section */}
        {entry.aiResponse && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8 p-4 rounded-2xl bg-wisteria-50 border border-wisteria-200"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-3">
              AI Reflection
            </h2>
            <p className="font-body text-sm text-primary leading-relaxed break-words [overflow-wrap:anywhere]">
              {entry.aiResponse}
            </p>
          </motion.div>
        )}

        {entry.homeChatCoachSummary && String(entry.homeChatCoachSummary).trim() && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="mb-8 p-4 rounded-2xl bg-theme-light border border-theme-secondary"
          >
            <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-3">
              Coaching chat recap
            </h2>
            <p className="font-body text-sm text-primary leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {String(entry.homeChatCoachSummary).trim()}
            </p>
            {entry.homeChatCoachSummaryAt && (
              <p className="font-body text-xs text-secondary/60 mt-2">
                Saved {new Date(entry.homeChatCoachSummaryAt).toLocaleString()}
              </p>
            )}
          </motion.div>
        )}

        {/* Comments Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <h2 className="font-body text-xs uppercase tracking-[0.16em] text-secondary/70 mb-4 text-center">
            Comments:
          </h2>

          {comments.length > 0 && (
            <div className="space-y-3 mb-6">
              {comments.map((comment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.05 }}
                  className="p-4 rounded-2xl bg-theme-light border border-theme-secondary"
                >
                  <p className="font-body text-sm text-primary mb-2 break-words [overflow-wrap:anywhere]">{comment.text}</p>
                  <p className="font-body text-xs text-secondary/60">
                    {formatDisplayDate(comment.date)}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          {/* Add Comment Input */}
          <div className="space-y-3">
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="bg-theme-light border-theme-secondary rounded-2xl p-4 focus:ring-theme-primary font-body min-h-[100px]"
              data-testid="comment-input"
            />
            <button
              onClick={handleSaveComment}
              disabled={!commentText.trim() || isSavingComment}
              className="w-full bg-wisteria-200 text-wisteria-900 rounded-full px-8 py-3 font-body font-medium transition-all hover:bg-wisteria-300 hover:-translate-y-0.5 shadow-sm disabled:opacity-50"
              data-testid="save-comment-btn"
            >
              {isSavingComment ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </span>
              ) : (
                'Save Comment'
              )}
            </button>
          </div>
        </motion.div>

      </div>
    </PageWrapper>
  );
};

export default HistoricalView;
